import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { BrandLogo, FirmLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ArrowRight, ChevronDown, ChevronRight, Users, ShieldCheck, Boxes, Store, Truck, Globe, KeyRound } from "lucide-react";
import { cn } from "@/lib/utils";
import { sectorIcon, groupSectors, sectorGroupIcon } from "@/lib/sectors";
import { activeLoginTheme, activeLoginLayout, activeLoginTexts, LOGIN_THEMES, LOGIN_LAYOUTS, LOGIN_TEXT_DEFAULTS } from "@/lib/company";

const DEMO_PASSWORD = "Groot!2026";

const FEATURES = [
  { icon: Store, label: "Perakende POS", sub: "Hızlı kasa" },
  { icon: Boxes, label: "Depo & Stok", sub: "Çok lokasyon" },
  { icon: Globe, label: "E-Ticaret", sub: "Pazaryeri" },
  { icon: Truck, label: "Lojistik", sub: "Sevkiyat" },
];
const STATS_ICONS = null;

// Giriş sol marka paneli — yerleşim şablonuna göre içerik (klasik = varsayılan). Yazılar düzenlenebilir.
function LoginBrandMiddle({ layout, txt }) {
  const t = txt || LOGIN_TEXT_DEFAULTS;
  const feats = (t.features && t.features.length ? t.features : LOGIN_TEXT_DEFAULTS.features);
  const stats = (t.stats && t.stats.length ? t.stats : LOGIN_TEXT_DEFAULTS.stats);
  const center = layout === "merkez";
  return (
    <div className={cn("relative z-10 max-w-md rise-in", center && "flex flex-col items-center text-center")}
      style={{ animationDelay: "0.08s" }} data-testid={`login-layout-${layout}`}>
      <h1 className="font-heading text-6xl font-black leading-[1.06] tracking-normal">
        {t.title}<br /><span className="text-brand-orange">{t.title_accent}</span>
      </h1>
      <p className="mt-6 max-w-sm text-lg font-light leading-relaxed text-emerald-100/80">{t.subtitle}</p>

      {layout === "klasik" && (
        <div className="mt-10 grid grid-cols-2 gap-3">
          {feats.map((f, i) => {
            const Icon = (FEATURES[i] || FEATURES[0]).icon;
            return (
              <div key={i} className="rounded-2xl border border-white/15 bg-white/[0.07] p-4 backdrop-blur-2xl">
                <Icon className="h-5 w-5 text-brand-orange" />
                <p className="mt-2 text-sm font-bold">{f.label}</p>
                <p className="text-[11px] text-emerald-200/70">{f.sub}</p>
              </div>
            );
          })}
        </div>
      )}

      {layout === "liste" && (
        <div className="mt-8 space-y-2.5">
          {feats.map((f, i) => {
            const Icon = (FEATURES[i] || FEATURES[0]).icon;
            return (
              <div key={i} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.05] px-4 py-2.5 backdrop-blur-xl">
                <Icon className="h-5 w-5 shrink-0 text-brand-orange" />
                <div><p className="text-sm font-bold leading-none">{f.label}</p><p className="mt-1 text-[11px] text-emerald-200/70">{f.sub}</p></div>
              </div>
            );
          })}
        </div>
      )}

      {layout === "istatistik" && (
        <div className="mt-10 grid grid-cols-3 gap-3">
          {stats.map((s, i) => (
            <div key={i} className="rounded-2xl border border-white/15 bg-white/[0.07] p-4 backdrop-blur-2xl">
              <p className="font-heading text-2xl font-black text-brand-orange">{s.big}</p>
              <p className="mt-1 text-[11px] font-medium text-emerald-100/80">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {layout === "kartust" && (
        <div className="mt-8 flex flex-wrap gap-2">
          {feats.map((f, i) => (
            <span key={i} className="rounded-full border border-white/15 bg-white/[0.07] px-3.5 py-1.5 text-xs font-bold backdrop-blur-xl">{f.label}</span>
          ))}
        </div>
      )}

      {layout === "bolme" && (
        <div className="mt-8 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          {feats.map((f, i) => {
            const Icon = (FEATURES[i] || FEATURES[0]).icon;
            return (
              <div key={i} className="flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/[0.05] px-3.5 py-2.5 backdrop-blur-xl">
                <Icon className="h-5 w-5 shrink-0 text-brand-orange" />
                <span className="text-sm font-bold">{f.label}</span>
              </div>
            );
          })}
        </div>
      )}

      {layout === "vitrin" && (
        <div className="mt-8">
          <div className="rounded-3xl border border-white/15 bg-gradient-to-br from-white/[0.14] to-white/[0.04] p-6 backdrop-blur-2xl">
            {(() => { const Icon = (FEATURES[0] || FEATURES[0]).icon; return <Icon className="h-9 w-9 text-brand-orange" />; })()}
            <p className="mt-3 font-heading text-2xl font-black">{feats[0]?.label}</p>
            <p className="mt-1 text-sm text-emerald-100/80">{feats[0]?.sub}</p>
          </div>
          <div className="mt-4 flex gap-6">
            {stats.map((s, i) => (
              <div key={i}><p className="font-heading text-xl font-black text-brand-orange">{s.big}</p><p className="text-[11px] text-emerald-100/70">{s.label}</p></div>
            ))}
          </div>
        </div>
      )}

      {layout === "serit" && (
        <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-2xl border border-white/12 bg-white/[0.06] px-5 py-3.5 backdrop-blur-xl">
          {feats.map((f, i) => {
            const Icon = (FEATURES[i] || FEATURES[0]).icon;
            return (
              <div key={i} className="flex items-center gap-1.5"><Icon className="h-4 w-4 text-brand-orange" /><span className="text-xs font-bold">{f.label}</span></div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Login() {
  const { login, verify2fa, personnelLogin } = useAuth();
  const nav = useNavigate();
  const [brandLogo, setBrandLogo] = useState("");  // firmanın kendi logosu; yoksa yerleşik firma logosu gösterilir (program logosu KULLANILMAZ)
  const [progLogoOk, setProgLogoOk] = useState(true);  // program logosu görseli varsa login sol-alt rozette gösterilir; yoksa Groot ONE wordmark
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginMode, setLoginMode] = useState("system");  // system (e-posta) | personnel (firma+kullanıcı adı)
  const [pServer, setPServer] = useState("");
  const [pUsername, setPUsername] = useState("");
  const [pPassword, setPPassword] = useState("");
  const [servers, setServers] = useState([]);           // personel girişinde firma (sunucu) listesi
  const [needServerPick, setNeedServerPick] = useState(false);
  useEffect(() => {
    api.get("/auth/servers").then((r) => {
      const s = r.data?.servers || [];
      setServers(s);
      if (s.length === 1) setPServer(s[0].slug);
    }).catch(() => {});
  }, []);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("credentials");
  const [companies, setCompanies] = useState([]);
  const [challengeId, setChallengeId] = useState(null);
  const [emailMasked, setEmailMasked] = useState("");
  const [phoneMasked, setPhoneMasked] = useState("");
  const [channel, setChannel] = useState("email");
  const [code, setCode] = useState("");
  const [actSerial, setActSerial] = useState("");
  const [actEmail, setActEmail] = useState("");
  const [actPass, setActPass] = useState("");
  const [actPass2, setActPass2] = useState("");
  const [recStep, setRecStep] = useState("request");
  const [recId, setRecId] = useState("");
  const [recChannel, setRecChannel] = useState("email");
  const [recTarget, setRecTarget] = useState("");
  const [recCode, setRecCode] = useState("");
  const [recToken, setRecToken] = useState("");
  const [recPass, setRecPass] = useState("");
  const recStart = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/recover/start", { identifier: email.trim(), channel: recChannel });
      setRecId(data.recovery_id); setRecTarget(data.target_masked || ""); setRecChannel(data.channel || recChannel); setRecStep("code");
      toast.info(data.message);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "İşlem başarısız"); } finally { setLoading(false); }
  };
  const recVerify = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/recover/verify", { recovery_id: recId, code: recCode });
      setRecToken(data.reset_token); setRecStep("reset");
      toast.success("Kod doğrulandı — yeni şifrenizi belirleyin");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Kod hatalı"); } finally { setLoading(false); }
  };
  const recReset = async () => {
    if (recPass.length < 6) return toast.error("Yeni şifre en az 6 karakter olmalı");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/recover/reset", { recovery_id: recId, reset_token: recToken, new_password: recPass });
      toast.success(data.message || "Şifreniz güncellendi");
      setPassword(recPass); setStep("credentials"); setRecStep("request"); setRecCode(""); setRecToken(""); setRecPass("");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "İşlem başarısız"); } finally { setLoading(false); }
  };
  const [showDemo, setShowDemo] = useState(false);
  const [demoAccounts, setDemoAccounts] = useState([]);
  const [demoEnabled, setDemoEnabled] = useState(false);
  const [sector, setSector] = useState(() => { try { return localStorage.getItem("groot_sector") || ""; } catch { return ""; } });
  const [sectorList, setSectorList] = useState([]);
  const [openSecGroups, setOpenSecGroups] = useState({});
  const [lt, setLt] = useState(() => activeLoginTheme());
  const [layout, setLayout] = useState(() => activeLoginLayout());
  const [txt, setTxt] = useState(() => activeLoginTexts());
  const pickSector = (k) => { setSector(k); try { localStorage.setItem("groot_sector", k); } catch { /* yoksay */ } };
  useEffect(() => {
    api.get("/sectors/public").then((r) => {
      const list = r.data || [];
      setSectorList(list);
      setSector((cur) => { const next = (cur && list.some((s) => s.code === cur)) ? cur : (list[0]?.code || ""); try { localStorage.setItem("groot_sector", next); } catch (e) {} return next; });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/demo-accounts/public").then((r) => {
      setDemoEnabled(!!r.data?.enabled);
      setDemoAccounts(r.data?.accounts || []);
    }).catch(() => { setDemoEnabled(false); setDemoAccounts([]); });
  }, []);

  // Giriş ekranı teması + yerleşim şablonu (Big BOSS'un Tasarım Stüdyosu ayarı — public)
  useEffect(() => {
    api.get("/settings/branding").then((r) => {
      setBrandLogo(r.data?.logo_url || "");
      const tn = r.data?.login_theme; const ln = r.data?.login_layout;
      if (tn && LOGIN_THEMES[tn]) { try { localStorage.setItem("groot_login_theme", tn); } catch (e) {} setLt(LOGIN_THEMES[tn]); }
      if (ln && LOGIN_LAYOUTS[ln]) { try { localStorage.setItem("groot_login_layout", ln); } catch (e) {} setLayout(ln); }
      const tx = r.data?.login_texts;
      if (tx && typeof tx === "object") { try { localStorage.setItem("groot_login_texts", JSON.stringify(tx)); } catch (e) {} setTxt({ ...LOGIN_TEXT_DEFAULTS, ...tx }); }
    }).catch(() => {});
  }, []);

  const doLogin = async (mail, pass, tenantArg) => {
    setLoading(true);
    try {
      if (!tenantArg) { try { localStorage.removeItem("groot_tenant"); } catch (e) {} }
      const res = await login(mail.trim(), pass, sector, tenantArg);
      if (res.requiresTenantSelection) {
        setCompanies(res.companies || []);
        setStep("tenant");
        toast.info("Bu e-posta birden çok firmaya kayıtlı — lütfen firma seçin");
      } else if (res.twofaRequired) {
        setChallengeId(res.challengeId);
        setEmailMasked(res.emailMasked);
        setPhoneMasked(res.phoneMasked || "");
        setChannel(res.channel || "email");
        setStep("otp");
        toast.info(res.channel === "sms" ? "Doğrulama kodu telefonunuza SMS ile gönderildi" : "Doğrulama kodu e-postanıza gönderildi");
      } else {
        toast.success(`Hoş geldin, ${res.user.name}`);
        nav("/");
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Giriş başarısız");
    } finally {
      setLoading(false);
    }
  };

  const submit = (e) => { e.preventDefault(); doLogin(email, password, servers.length === 1 && servers[0]?.slug === "groot" ? servers[0].slug : undefined); };
  const demoLogin = (mail) => { setEmail(mail); setPassword(DEMO_PASSWORD); doLogin(mail, DEMO_PASSWORD); };

  const submitPersonnel = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await personnelLogin(pServer.trim(), pUsername.trim(), pPassword, sector);
      if (res.requiresServerSelection) {
        setServers(res.servers || []);
        setPServer("");
        setNeedServerPick(true);
        toast.info("Birden fazla firmada kayıtlısınız — lütfen firmanızı seçin.");
        return;
      }
      if (res.twofaRequired) {
        setChallengeId(res.challengeId);
        setEmailMasked(res.emailMasked);
        setPhoneMasked(res.phoneMasked || "");
        setChannel(res.channel || "email");
        setStep("otp");
        toast.info(res.channel === "sms" ? "Doğrulama kodu telefonunuza SMS ile gönderildi" : "Doğrulama kodu e-postanıza gönderildi");
      } else {
        toast.success(`Hoş geldin, ${res.user.name}`);
        nav("/");
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Giriş başarısız");
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await verify2fa(challengeId, code.trim());
      toast.success(`Hoş geldin, ${u.name}`);
      nav("/");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Doğrulama başarısız");
    } finally {
      setLoading(false);
    }
  };

  const backToLogin = () => { setStep("credentials"); setCode(""); setChallengeId(null); };

  useEffect(() => {
    try { const p = new URLSearchParams(window.location.search); if (p.get("aktivasyon") === "1" || p.get("activate") === "1") setStep("activate"); } catch (e) { /* yoksay */ }
  }, []);

  const submitActivate = async (e) => {
    e.preventDefault();
    if (actPass.length < 6) return toast.error("Şifre en az 6 karakter olmalı");
    if (actPass !== actPass2) return toast.error("Şifreler eşleşmiyor");
    setLoading(true);
    try {
      const { data } = await api.post("/activate", { serial_key: actSerial.trim(), email: actEmail.trim().toLowerCase(), password: actPass });
      toast.success(data.message || "Hesabınız etkinleştirildi");
      setEmail(actEmail.trim().toLowerCase()); setPassword("");
      setActSerial(""); setActEmail(""); setActPass(""); setActPass2("");
      setStep("credentials");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Aktivasyon başarısız");
    } finally { setLoading(false); }
  };

  // Giriş ekranı ön-oturum: logo PUBLIC uçtan çekilir. YALNIZ firma logosu; yoksa yerleşik firma logosu (program logosu KULLANILMAZ).
  const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
  const loginLogoUrl = brandLogo ? `${API_BASE}/api/settings/branding/logo` : "";

  return (
    <div className="grid min-h-screen w-full overflow-hidden bg-slate-50 lg:grid-cols-[0.92fr_1fr]">
      {/* ---------- Sol: karizmatik marka paneli ---------- */}
      <div className="relative hidden flex-col justify-between overflow-hidden bg-[#04241C] px-10 py-8 text-white lg:flex" style={lt.vars} data-testid="login-brand-panel">
        {/* hareketli mesh gradient blob'ları (renk: giriş teması) */}
        <div className="pointer-events-none absolute -left-32 -top-24 h-[28rem] w-[28rem] rounded-full opacity-60 blur-[110px] animate-float-slow" style={{ backgroundColor: "var(--lb1)" }} />
        <div className="pointer-events-none absolute -right-24 top-1/3 h-96 w-96 rounded-full opacity-30 blur-[120px] animate-float-slower" style={{ backgroundColor: "var(--lb2)" }} />
        <div className="pointer-events-none absolute bottom-0 left-1/4 h-80 w-80 rounded-full opacity-20 blur-[120px] animate-float-slow" style={{ backgroundColor: "var(--lb3)" }} />
        {/* grain */}
        <div className="login-grain pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-overlay" />
        {/* ince ızgara */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "56px 56px" }} />

        {/* üst: logo + wordmark */}
        <div className="relative z-10 -mt-2 rise-in">
          <div className="inline-block rounded-xl bg-white/[0.17] px-3.5 py-2.5 shadow-lg ring-1 ring-inset ring-white/25 backdrop-blur-sm">
            <FirmLogo size={62} logoUrl={loginLogoUrl} />
          </div>
        </div>

        {/* orta: yerleşim şablonuna göre içerik */}
        <LoginBrandMiddle layout={layout} txt={txt} />

        {/* alt: imza + logo (sağda) */}
        <div className="relative z-10 -translate-y-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 font-heading text-[15px] font-semibold tracking-wide text-emerald-200/70">
            <ShieldCheck className="h-[18px] w-[18px]" /> Groot ONE · Bulut ERP · {new Date().getFullYear()}
          </div>
          <div className="rounded-xl bg-white/95 px-6 py-4 shadow-lg" data-testid="login-program-logo">
            {progLogoOk
              ? <img src={`${API_BASE}/api/platform/program-logo/image`} alt="Program Logosu" className="h-11 w-auto max-w-[240px] object-contain" onError={() => setProgLogoOk(false)} />
              : <BrandLogo variant="light" size={34} showText={false} />}
          </div>
        </div>
      </div>

      {/* ---------- Sağ: form ---------- */}
      <div className="relative z-10 flex w-full flex-col items-center justify-center p-6 sm:p-8 lg:px-16 lg:py-8">
        <div className="w-full max-w-2xl rise-in">
          <div className="mb-6 lg:hidden">
            <BrandLogo variant="light" size={30} />
          </div>

          <div className="rounded-[2rem] border border-slate-100 bg-white p-8 shadow-xl sm:p-12">
            {step === "activate" ? (
              <>
                <p className="overline mb-1 text-brand-orange">Hesap Aktivasyonu</p>
                <h2 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Hesabınızı Etkinleştirin</h2>
                <p className="mb-6 mt-2 text-sm text-slate-500">Size e-posta ile gönderilen <b>seri aktivasyon anahtarını</b> girip kendi şifrenizi belirleyin.</p>
                <form onSubmit={submitActivate} className="space-y-4">
                  <div>
                    <Label htmlFor="act-serial" className="mb-1.5 block text-sm font-semibold text-slate-700">Seri Aktivasyon Anahtarı</Label>
                    <Input id="act-serial" data-testid="activate-serial" value={actSerial} onChange={(e) => setActSerial(e.target.value.toUpperCase())} placeholder="GROOT-XXXX-XXXX-XXXX-XXXX" required autoFocus className="h-12 rounded-xl font-mono tracking-widest" />
                  </div>
                  <div>
                    <Label htmlFor="act-email" className="mb-1.5 block text-sm font-semibold text-slate-700">Yönetici E-posta</Label>
                    <Input id="act-email" data-testid="activate-email" type="email" value={actEmail} onChange={(e) => setActEmail(e.target.value)} placeholder="patron@firma.com" required className="h-12 rounded-xl" />
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="act-pass" className="mb-1.5 block text-sm font-semibold text-slate-700">Yeni Şifre</Label>
                      <Input id="act-pass" data-testid="activate-password" type="password" value={actPass} onChange={(e) => setActPass(e.target.value)} placeholder="••••••••" required className="h-12 rounded-xl" />
                    </div>
                    <div>
                      <Label htmlFor="act-pass2" className="mb-1.5 block text-sm font-semibold text-slate-700">Şifre (Tekrar)</Label>
                      <Input id="act-pass2" data-testid="activate-password2" type="password" value={actPass2} onChange={(e) => setActPass2(e.target.value)} placeholder="••••••••" required className="h-12 rounded-xl" />
                    </div>
                  </div>
                  <Button type="submit" data-testid="activate-submit" disabled={loading} className="h-12 w-full rounded-xl bg-brand-green text-base font-bold text-white shadow-lg hover:bg-brand-green-600">
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Hesabımı Etkinleştir
                  </Button>
                  <button type="button" data-testid="activate-back" onClick={() => setStep("credentials")} className="w-full text-center text-sm text-slate-500 transition-colors hover:text-brand-green">← Girişe geri dön</button>
                </form>
              </>
            ) : step === "otp" ? (
              <>
                <p className="overline mb-1 text-brand-orange">İki Adımlı Doğrulama</p>
                <h2 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Kodu Girin</h2>
                <p className="mb-7 mt-2 text-sm text-slate-500">
                  <b>{channel === "sms" ? (phoneMasked || "telefonunuza") : emailMasked}</b> {channel === "sms" ? "numarasına SMS ile" : "adresine"} gönderilen 6 haneli kodu girin.
                </p>
                <form onSubmit={submitOtp} className="space-y-5">
                  <div>
                    <Label htmlFor="otp" className="mb-1.5 block text-sm font-semibold text-slate-700">Doğrulama Kodu</Label>
                    <Input id="otp" data-testid="login-otp" inputMode="numeric" autoFocus autoComplete="one-time-code" maxLength={6}
                      value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                      placeholder="______" required
                      className="h-14 text-center text-2xl font-bold tracking-[0.5em]" />
                  </div>
                  <Button type="submit" data-testid="login-otp-submit" disabled={loading || code.length < 6}
                    className="h-12 w-full rounded-xl bg-brand-orange text-base font-bold text-white shadow-lg shadow-orange-500/20 hover:bg-orange-600">
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Doğrula ve Giriş Yap
                  </Button>
                  <button type="button" data-testid="login-otp-back" onClick={backToLogin}
                    className="w-full text-center text-sm text-slate-500 transition-colors hover:text-brand-green">
                    ← Girişe geri dön
                  </button>
                </form>
              </>
            ) : step === "tenant" ? (
              <>
                <p className="overline mb-1 text-brand-orange">Firma Seçimi</p>
                <h2 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Firmanızı Seçin</h2>
                <p className="mb-6 mt-2 text-sm text-slate-500">Bu e-posta birden çok firmaya kayıtlı. Giriş yapmak istediğiniz firmayı seçin.</p>
                <div className="space-y-2.5" data-testid="tenant-select">
                  {companies.map((c) => (
                    <button key={c.slug} type="button" data-testid={`tenant-option-${c.slug}`} disabled={loading}
                      onClick={() => doLogin(email, password, c.slug)}
                      className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-left shadow-sm transition-all hover:border-brand-green hover:bg-emerald-50/40 disabled:opacity-50">
                      <span className="flex items-center gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-green/10 text-brand-green"><Store className="h-5 w-5" /></span>
                        <span className="text-sm font-bold text-slate-800">{c.name}</span>
                      </span>
                      <ArrowRight className="h-4 w-4 text-slate-400" />
                    </button>
                  ))}
                </div>
                <button type="button" data-testid="tenant-back" onClick={() => { setStep("credentials"); setCompanies([]); }}
                  className="mt-4 w-full text-center text-sm text-slate-500 transition-colors hover:text-brand-green">← Girişe geri dön</button>
              </>
            ) : step === "recover" ? (
              <>
                <p className="overline mb-1 text-brand-orange">Şifremi Unuttum</p>
                <h2 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Şifremi Sıfırla</h2>
                <p className="mb-6 mt-2 text-sm text-slate-500">Kayıtlı <b>e-posta adresinizi</b> veya <b>telefon numaranızı</b> girin; size 6 haneli bir doğrulama kodu gönderelim. Kodu doğrulayınca yeni şifrenizi belirlersiniz ve hemen giriş yapabilirsiniz.</p>
                {recStep === "request" ? (
                  <div className="space-y-4">
                    <div>
                      <Label className="mb-1.5 block text-sm font-semibold text-slate-700">E-posta veya Telefon</Label>
                      <Input data-testid="recover-email" type="text" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="patron@firma.com veya 0555 000 00 00" className="h-12 rounded-xl" />
                    </div>
                    <div>
                      <Label className="mb-1.5 block text-sm font-semibold text-slate-700">Kod nereye gelsin?</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {[["sms", "Telefonuma (SMS)"], ["email", "E-postama"]].map(([v, l]) => (
                          <button key={v} type="button" data-testid={`recover-channel-${v}`} onClick={() => setRecChannel(v)}
                            className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${recChannel === v ? "border-brand-green bg-brand-green/5 text-brand-green ring-1 ring-brand-green/30" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>{l}</button>
                        ))}
                      </div>
                    </div>
                    <Button data-testid="recover-start" onClick={recStart} disabled={loading || !email.trim()} className="h-12 w-full rounded-xl bg-brand-green text-base font-bold text-white hover:bg-brand-green-600">
                      {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Doğrulama Kodu Gönder
                    </Button>
                  </div>
                ) : recStep === "code" ? (
                  <div className="space-y-4">
                    <p className="text-sm text-slate-500">Kod {recChannel === "sms" ? "telefonunuza" : "e-postanıza"} {recTarget ? <b>({recTarget})</b> : null} gönderildi. 10 dakika geçerli.</p>
                    <Input data-testid="recover-code" inputMode="numeric" maxLength={6} value={recCode} onChange={(e) => setRecCode(e.target.value.replace(/\D/g, ""))} placeholder="______" className="h-14 text-center text-2xl font-bold tracking-[0.5em]" />
                    <Button data-testid="recover-verify" onClick={recVerify} disabled={loading || recCode.length < 6} className="h-12 w-full rounded-xl bg-brand-orange text-base font-bold text-white hover:bg-orange-600">
                      {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Kodu Doğrula
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <Label className="mb-1.5 block text-sm font-semibold text-slate-700">Yeni Şifre</Label>
                      <Input data-testid="recover-newpass" type="password" value={recPass} onChange={(e) => setRecPass(e.target.value)} placeholder="En az 6 karakter" className="h-12 rounded-xl" />
                    </div>
                    <Button data-testid="recover-reset" onClick={recReset} disabled={loading || recPass.length < 6} className="h-12 w-full rounded-xl bg-brand-green text-base font-bold text-white hover:bg-brand-green-600">
                      {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Şifremi Güncelle
                    </Button>
                  </div>
                )}
                <button type="button" data-testid="recover-back" onClick={() => { setStep("credentials"); setRecStep("request"); setRecCode(""); }} className="mt-4 w-full text-center text-sm text-slate-500 transition-colors hover:text-brand-green">← Girişe geri dön</button>
              </>
            ) : (
              <>
                <p className="overline mb-1 text-brand-orange">{loginMode === "personnel" ? "Personel Girişi" : "Yönetim Paneli"}</p>
                <h2 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Giriş Yap</h2>
                <p className="mb-4 mt-2 text-sm text-slate-500">{loginMode === "personnel" ? "Firmanızı seçin, kullanıcı adı ve şifrenizle girin (kasiyer, mağaza müdürü, muhasebe, depo, satın alma…)." : "Sisteme erişmek için bilgilerinizi girin."}</p>

                <div data-testid="login-mode-toggle" className="mx-auto mb-6 flex max-w-md rounded-xl border border-slate-200 bg-slate-50 p-1">
                  <button type="button" data-testid="login-mode-system" onClick={() => setLoginMode("system")}
                    className={cn("flex-1 rounded-lg px-3 py-2 text-sm font-bold transition-colors", loginMode === "system" ? "bg-white text-brand-green shadow-sm ring-1 ring-slate-200" : "text-slate-500 hover:text-slate-700")}>Yönetim / E-posta</button>
                  <button type="button" data-testid="login-mode-personnel" onClick={() => setLoginMode("personnel")}
                    className={cn("flex-1 rounded-lg px-3 py-2 text-sm font-bold transition-colors", loginMode === "personnel" ? "bg-white text-brand-orange shadow-sm ring-1 ring-slate-200" : "text-slate-500 hover:text-slate-700")}>Personel</button>
                </div>

                {sectorList.length > 0 && (() => {
                  const renderChip = (s) => {
                    const Icon = sectorIcon(s.icon);
                    const active = sector === s.code;
                    return (
                      <button key={s.code} type="button" data-testid={`sector-option-${s.code}`} onClick={() => pickSector(s.code)} aria-pressed={active} title={s.name}
                        className={cn("inline-flex w-full items-center gap-2.5 rounded-xl border px-3.5 py-3 text-left text-[13px] font-semibold transition-all",
                          active ? "border-brand-green bg-brand-green/5 text-brand-green shadow-sm ring-1 ring-brand-green/30" : "border-slate-200 bg-white text-slate-600 hover:border-brand-green/40 hover:bg-emerald-50/40")}>
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: active ? s.color : `${s.color}1a`, color: active ? "#fff" : s.color }}>
                          <Icon className="h-5 w-5" />
                        </span>
                        <span className="min-w-0 flex-1 whitespace-normal break-words leading-tight">{s.name}</span>
                      </button>
                    );
                  };
                  const FLAT = sectorList.length <= 7;
                  const secGroups = groupSectors(sectorList);
                  const isOpen = (grp, i) => (grp.group in openSecGroups ? openSecGroups[grp.group] : (i === 0 || grp.group === "⭐ Özel" || grp.items.some((s) => s.code === sector)));
                  const anyClosed = secGroups.some((g, i) => !isOpen(g, i));
                  return (
                    <div className="mb-6" data-testid="sector-select">
                      <div className="mb-1.5 flex items-center justify-between">
                        <Label className="block text-sm font-semibold text-slate-700">Yönetim Sektörü</Label>
                        {!FLAT && (
                          <button type="button" data-testid="sector-groups-toggle" onClick={() => setOpenSecGroups(Object.fromEntries(secGroups.map((g) => [g.group, anyClosed])))}
                            className="text-xs font-semibold text-brand-green hover:underline">{anyClosed ? "Tümünü Aç" : "Tümünü Kapat"}</button>
                        )}
                      </div>
                      {FLAT ? (
                        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2" data-testid="sector-options">
                          {sectorList.map(renderChip)}
                        </div>
                      ) : (
                        <div className={cn("space-y-2", sectorList.length > 8 ? "max-h-72 overflow-y-auto pr-1" : "")} data-testid="sector-options">
                          {secGroups.map((grp, gi) => {
                            const opened = isOpen(grp, gi);
                            const GroupIcon = sectorGroupIcon(grp.group);
                            const isStar = grp.group === "⭐ Özel";
                            return (
                              <div key={grp.group} className="rounded-xl border border-slate-200 bg-white/70">
                                <button type="button" data-testid={`sector-group-toggle-${grp.group}`} onClick={() => setOpenSecGroups((o) => ({ ...o, [grp.group]: !opened }))}
                                  className="flex w-full items-center gap-2 px-3 py-2 text-left">
                                  <GroupIcon className={cn("h-4 w-4 shrink-0", isStar ? "text-amber-500" : "text-brand-green")} />
                                  <span className={cn("flex-1 text-xs font-bold", isStar ? "text-amber-600" : "text-slate-600")}>{grp.group}</span>
                                  <span className="text-[10px] font-semibold text-slate-400">{grp.items.length}</span>
                                  {opened ? <ChevronDown className="h-3.5 w-3.5 text-slate-400" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400" />}
                                </button>
                                {opened && (
                                  <div className="grid grid-cols-1 gap-2.5 border-t border-slate-100 p-2 sm:grid-cols-2">
                                    {grp.items.map(renderChip)}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {loginMode === "personnel" ? (
                <div className="mx-auto max-w-md">
                  {pServer && (
                    <div data-testid="personnel-firm-logo" className="mb-5 flex flex-col items-center gap-2 rounded-2xl border border-brand-green/20 bg-brand-green/5 px-4 py-4">
                      <img src={`${API_BASE}/api/auth/tenant-logo/${encodeURIComponent(pServer)}`} alt="Firma logosu"
                        className="h-14 w-auto max-w-[200px] object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      <p className="font-heading text-base font-bold text-slate-800" data-testid="personnel-firm-name">{(servers.find((s) => s.slug === pServer) || {}).name || pServer}</p>
                    </div>
                  )}
                <form onSubmit={submitPersonnel} className="space-y-5">
                  {(servers.length !== 1 || needServerPick) && (
                  <div>
                    <Label htmlFor="p-server" className="mb-1.5 block text-sm font-semibold text-slate-700">Firma (Sunucu)</Label>
                    {servers.length > 0 ? (
                      <select id="p-server" data-testid="personnel-server" value={pServer} required
                        onChange={(e) => { setPServer(e.target.value); setNeedServerPick(false); }}
                        className="h-12 w-full rounded-xl border border-input bg-white px-3 text-sm focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-green/10">
                        <option value="">Firmanızı seçin…</option>
                        {servers.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
                      </select>
                    ) : (
                      <Input id="p-server" data-testid="personnel-server" value={pServer}
                        onChange={(e) => setPServer(e.target.value)} placeholder="Firma / sunucu adı"
                        className="h-12 rounded-xl focus-visible:ring-4 focus-visible:ring-brand-green/10" />
                    )}
                    {needServerPick && <p className="mt-1 text-[11px] font-semibold text-amber-600">Bu kullanıcı adı birden çok firmada var — lütfen firmanızı seçin.</p>}
                  </div>
                  )}
                  <div>
                    <Label htmlFor="p-username" className="mb-1.5 block text-sm font-semibold text-slate-700">Kullanıcı Adı</Label>
                    <Input id="p-username" data-testid="personnel-username" value={pUsername} autoComplete="username"
                      onChange={(e) => setPUsername(e.target.value)} placeholder="ör. kasa1" required
                      className="h-12 rounded-xl focus-visible:ring-4 focus-visible:ring-brand-green/10" />
                  </div>
                  <div>
                    <Label htmlFor="p-pass" className="mb-1.5 block text-sm font-semibold text-slate-700">Şifre</Label>
                    <Input id="p-pass" data-testid="personnel-password" type="password" autoComplete="current-password" value={pPassword}
                      onChange={(e) => setPPassword(e.target.value)} placeholder="••••••••" required
                      className="h-12 rounded-xl focus-visible:ring-4 focus-visible:ring-brand-green/10" />
                  </div>
                  <Button type="submit" data-testid="personnel-submit" disabled={loading}
                    className="group h-12 w-full rounded-xl bg-brand-orange text-lg font-bold text-white shadow-lg shadow-orange-500/25 transition-all hover:bg-orange-600">
                    {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Personel Girişi
                    {!loading && <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />}
                  </Button>
                </form>
                </div>
                ) : (
                <form onSubmit={submit} className="mx-auto max-w-md space-y-5">
                  <div>
                    <Label htmlFor="email" className="mb-1.5 block text-sm font-semibold text-slate-700">E-posta</Label>
                    <Input id="email" data-testid="login-email" type="email" value={email}
                      onChange={(e) => setEmail(e.target.value)} placeholder="ornek@groot.com" required
                      className="h-12 rounded-xl focus-visible:ring-4 focus-visible:ring-brand-green/10" />
                  </div>
                  <div>
                    <Label htmlFor="password" className="mb-1.5 block text-sm font-semibold text-slate-700">Şifre</Label>
                    <Input id="password" data-testid="login-password" type="password" autoComplete="current-password" value={password}
                      onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required
                      className="h-12 rounded-xl focus-visible:ring-4 focus-visible:ring-brand-green/10" />
                  </div>
                  <Button type="submit" data-testid="login-submit" disabled={loading}
                    className="group h-12 w-full rounded-xl bg-brand-orange text-lg font-bold text-white shadow-lg shadow-orange-500/25 transition-all hover:bg-orange-600">
                    {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Giriş Yap
                    {!loading && <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />}
                  </Button>
                </form>
                )}

                <button type="button" data-testid="login-activate-link" onClick={() => setStep("activate")}
                  className="mt-4 flex w-full items-center justify-center gap-1.5 text-sm font-semibold text-brand-green transition-colors hover:underline">
                  <KeyRound className="h-4 w-4" /> Seri anahtarınızla hesabınızı aktive edin
                </button>

                <button type="button" data-testid="login-recover-link" onClick={() => { setStep("recover"); setRecStep("request"); }}
                  className="mt-2 flex w-full items-center justify-center gap-1.5 text-sm font-semibold text-brand-green transition-colors hover:underline">
                  <ShieldCheck className="h-4 w-4" /> Şifremi unuttum
                </button>

                {/* Demo Hesapları */}
                {demoEnabled && demoAccounts.length > 0 && (
                <div className="mt-6">
                  <button type="button" data-testid="demo-accounts-toggle" onClick={() => setShowDemo((v) => !v)}
                    className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-100">
                    <span className="flex items-center gap-2"><Users className="h-4 w-4 text-brand-green" /> Demo Hesapları</span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${showDemo ? "rotate-180" : ""}`} />
                  </button>
                  {showDemo && (
                    <div data-testid="demo-accounts-list" className="mt-2 grid grid-cols-2 gap-2 rounded-2xl border border-slate-200 bg-slate-50/80 p-3">
                      {demoAccounts.map((a) => (
                        <button key={a.email} type="button" data-testid={`demo-login-${a.email}`} onClick={() => demoLogin(a.email)} disabled={loading}
                          className="flex flex-col items-start rounded-xl border border-slate-200 bg-white px-3 py-2 text-left shadow-sm transition-all hover:border-brand-green hover:shadow disabled:opacity-50">
                          <span className="text-sm font-bold text-slate-800">{a.label}</span>
                          <span className="truncate text-[10px] text-slate-400">{a.email}</span>
                        </button>
                      ))}
                      <p className="col-span-2 mt-1 text-center text-[10px] text-slate-400">Tek dokunuşla giriş · şifre otomatik ({DEMO_PASSWORD})</p>
                    </div>
                  )}
                </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
